import { useState } from "react";
import { useParams } from "react-router";
import { useOutletContext, useLocation } from "react-router-dom";

import { Box, Paper, Grid, Card, CardContent, Typography, Stack, Button } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";

import Avatar from "../../assets/img/avatar.png";
import Loader from "../../components/Loader";
import RetakeVideoPopUp from "../../components/PopUp/RetakeVideo";
import useGetSinglePhotoSession from "../../hooks/useGetSinglePhotoSession";
import useRetakeVideoMutation from "../../hooks/useRetakeVideoMutation";

const VideoDetail = () => {
  const userData = JSON.parse(localStorage.getItem("user_data"));
  const role = userData?.role;

  const { caseId } = useParams();

  const location = useLocation();
  const visitName =
    location.state?.visitName || new URLSearchParams(location.search).get("visitName");

  const { setActive, setBreadcrumbTitle } = useOutletContext();
  setActive("videos");
  setBreadcrumbTitle("Videos");

  const [modalRetake, setModalRetake] = useState(false);
  const [videoId, setVideoId] = useState(null);

  const { data: sessionData = {}, isLoading } = useGetSinglePhotoSession({ caseId });
  const retakeVideoMutation = useRetakeVideoMutation();

  const videos = sessionData.video || [];
  const selectedTrialAreas = sessionData.selected_trail_areas || "N/A";

  const isVideoHidden =
    (role === "CRO Admin" || role === "Cro_coordinator" || role === "Data Manager") &&
    sessionData.is_hide_video;

  const canRetake =
    (role === "Investigator" || role === "Inv_coordinator" || role === "Reviewer") &&
    !sessionData.has_next_visit_started;

  const handleRetakeVideo = () => {
    retakeVideoMutation.mutate(
      { videoId },
      {
        onSuccess: () => {
          setModalRetake(false);
        },
      }
    );
  };

  return (
    <>
      <Paper elevation={1} sx={{ p: 2, minHeight: "80vh" }}>
        {/* Header */}
        <Stack spacing={1} mb={2}>
          <Typography variant="h6">Videos</Typography>
          <Typography variant="body2" color="text.secondary">
            Visit Name: {visitName || "-"}
          </Typography>
        </Stack>

        {/* Loader */}
        {isLoading && (
          <Box display="flex" justifyContent="center" py={6}>
            <Loader />
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && videos.length === 0 && (
          <Box textAlign="center" py={6}>
            <Typography color="text.secondary">No videos attached to this session</Typography>
          </Box>
        )}

        {/* Video Grid */}
        {!isLoading && videos.length > 0 && (
          <Grid container spacing={2}>
            {videos.map((vid, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={vid.id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: 3,
                    },
                  }}
                >
                  {/* Video / Placeholder */}
                  <Box
                    sx={{
                      height: 220,
                      backgroundColor: "#000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isVideoHidden ? (
                      <Box
                        component="img"
                        src={Avatar}
                        alt="Hidden"
                        sx={{
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <Box
                        component="video"
                        controls
                        playsInline
                        src={vid.video_url}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    )}
                  </Box>

                  <CardContent>
                    <Stack spacing={0.75}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {vid.video_type}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        Trial: {selectedTrialAreas}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        Visit: {vid.photo_session?.visit_name || "N/A"}
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        Captured:{" "}
                        {vid.capture_datetime
                          ? new Date(vid.capture_datetime + "Z").toLocaleString()
                          : "-"}
                      </Typography>

                      {canRetake && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ReplayIcon />}
                          onClick={() => {
                            setVideoId(vid.id);
                            setModalRetake(true);
                          }}
                        >
                          Retake
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {modalRetake && (
        <RetakeVideoPopUp setModalRetake={setModalRetake} handleRetakeVideo={handleRetakeVideo} />
      )}
    </>
  );
};

export default VideoDetail;
